import moment from 'moment'

interface Opportunity {
  Name: string
  Account?: {
    Name: string
  }
  FGM_Base__Request_Number__c?: string
}

interface GranteeReportUploadFileNameParams {
  type: 'Grantee report'
  opportunity: Opportunity
  reportType?: string
  progressReportNumber?: number | null
  existingDocsCount?: number
}

interface ExecutiveSummaryUploadFileNameParams {
  type: 'Executive summary'
  opportunity: Opportunity
  existingDocsCount?: number
}

interface ApplicationUploadFileNameParams {
  type: 'Application'
  opportunity: Opportunity
  existingDocsCount?: number
}

interface FundingAgreementUploadFileNameParams {
  type: 'Funding agreement'
  opportunity: Opportunity
}

interface PreQualificationUploadFileNameParams {
  type: 'Pre-qualification'
  name: string
  existingDocsCount?: number
}

type GenerateUploadFileNameParams =
  | GranteeReportUploadFileNameParams
  | ExecutiveSummaryUploadFileNameParams
  | ApplicationUploadFileNameParams
  | FundingAgreementUploadFileNameParams
  | PreQualificationUploadFileNameParams

/**
 * Generates a file name based on document type and related metadata
 * @param params Parameters for file name generation
 * @param params.type Document type
 * @param params.opportunity Opportunity record
 * @param params.existingDocsCount Number of existing documents
 * @param params.reportType Report type (applicable for Grantee report)
 * @param params.progressReportNumber Progress report number (applicable for Grantee report)
 * @returns Generated file name
 */
export function generateUploadFileName (props: GenerateUploadFileNameParams): string {
  // Version number is based on existing documents count + 1
  const { opportunity, type } = props
  const submissionDateStr = moment(new Date()).format('YYYY-MM-DD')

  switch (type) {
    case 'Funding agreement':
      return `${transactionName('', opportunity)}.pdf`

    case 'Application': {
      const versionNumber = (props.existingDocsCount ?? 0) + 1
      return `Application V${versionNumber} ${submissionDateStr} ${opportunity.Name}.pdf`
    }

    case 'Grantee report': {
      const { reportType, progressReportNumber } = props
      const progressNum = progressReportNumber ? ` ${progressReportNumber}` : ''
      const versionNumber = (props.existingDocsCount ?? 0) + 1
      return `${reportType}${progressNum} V${versionNumber} ${submissionDateStr} ${opportunity.Name}.pdf`
    }

    case 'Executive summary': {
      const versionNumber = (props.existingDocsCount ?? 0) + 1
      return `Executive summary V${versionNumber} ${submissionDateStr}: ${opportunity.Name}.pdf`
    }

    case 'Pre-qualification': {
      const versionNumber = (props.existingDocsCount ?? 0) + 1
      return `Pre-qualification V${versionNumber} ${submissionDateStr}: ${props.name}.pdf`
    }

    default:
      throw new Error(`Unsupported document type: ${type}`)
  }
}

/**
 * Generates a transaction name from opportunity details
 * @param lang Language code (not used currently but kept for compatibility)
 * @param opportunity Opportunity object containing request number, account name and opportunity name
 * @returns Formatted transaction name string, truncated to 76 characters
 */
export function transactionName (lang: string, opportunity: Opportunity): string {
  const requestNumber = opportunity.FGM_Base__Request_Number__c || ''
  const accountName = opportunity.Account?.Name || ''
  const opportunityName = opportunity.Name

  let result = ''

  if (requestNumber && accountName) {
    result += `${requestNumber} - ${accountName}`
  } else if (requestNumber) {
    result += requestNumber
  } else if (accountName) {
    result += accountName
  }

  if (result) {
    result += `, ${opportunityName}`
  } else {
    result = opportunityName
  }

  return result.substring(0, 76).trim()
}
